import React, {useState, useEffect, useRef} from 'react';
import {
  faMicrophone,
  faMicrophoneSlash,
  faVideo,
  faVideoSlash,
} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

import Tooltip from '../components/Tooltip';
import Button, {ButtonSizes} from '../components/ButtonNew';
import Link from '../components/Link';
import {getSettings, setSettings} from './VideoRoomContainer';
import DuetAIFeedbackToggle, {getSavedAIFeedbackPref, setSavedAIFeedbackPref} from './DuetAIFeedbackToggle';
import RecordingConsentCheckbox from './RecordingConsentCheckbox';
import {useAppContext} from '../hooks';
import {
  canGiveRecordingConsent,
  canPreCheckRecordingConsent,
  hasRecordingConsent,
} from '../../../lib/utils/recordingConsent';
import {BLOCKED_CAMERA_ERROR, assertNotBlockedCamera, getAllowedVideoDevices} from './videoHelpers';

const InputLevel = ({stream}) => {
  const [level, setLevel] = useState(0);

  useEffect(() => {
    let animationFrameId;
    let audioContext;
    let source;
    let analyser;

    const getMicrophoneAccess = async () => {
      try {
        if (!stream) return;

        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        source = audioContext.createMediaStreamSource(stream);
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.minDecibels = -60;
        analyser.maxDecibels = -10;
        source.connect(analyser);
        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevel = () => {
          analyser.getByteFrequencyData(dataArray);
          const max = Math.max(...dataArray);
          setLevel(max);
          animationFrameId = requestAnimationFrame(updateLevel);
        };

        updateLevel();
      } catch (err) {
        console.error('Error accessing microphone', err);
      }
    };

    getMicrophoneAccess();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      if (source) source.disconnect();
      if (analyser) analyser.disconnect();
      if (audioContext) audioContext.close();
    };
  }, [stream]);

  return (
    <div className="flex items-center mt-2">
      {[...Array(10)].map((_, i) => (
        <div
          className="rounded h-2 w-4 mx-0.5"
          style={{backgroundColor: i < (level / 25) ? 'green' : 'lightgray'}}
        />
      ))}
    </div>
  );
};


const DuetSettings = ({
  includeAudio = true,
  includeVideo = true,
  hideAIFeedbackToggle = true,
  hideTroubleshooting,
  showRecordingConsent = false,
  submitText,
  onSubmit,
}) => {
  const [audioDevices, setAudioDevices] = useState([]);
  const [videoDevices, setVideoDevices] = useState([]);
  const [audioTracks, setAudioTracks] = useState([]);
  const [videoTracks, setVideoTracks] = useState([]);
  const [audioDevice, setAudioDevice] = useState(null);
  const [videoDevice, setVideoDevice] = useState(null);
  const [setupComplete, setSetupComplete] = useState(false);
  const [error, setError] = useState(null);
  const [enableAIFeedback, setEnableAIFeedback] = useState(() => getSavedAIFeedbackPref());
  const [stream, setStream] = useState(null);
  const streamRef = useRef(null);

  const videoRef = useRef();
  const {currentUser} = useAppContext();
  // Pre-ticked for users who haven't chosen yet, where that's valid consent
  const [allowRecordingConsent, setAllowRecordingConsent] = useState(() => {
    if (!showRecordingConsent || !canGiveRecordingConsent(currentUser)) return false;
    return hasRecordingConsent(currentUser) || canPreCheckRecordingConsent(currentUser);
  });

  const handleStream = (newStream) => {
    setStream(newStream);
    streamRef.current = newStream; // Store the stream in a ref for cleanup
  };

  const stopVideoAndAudio = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      handleStream(null);
    } else if (streamRef.current) {
      // use streamRef to stop tracks on unmount because states are cleared
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  const getMediaStream = async (videoDev, audioDev) => {
    try {
      stopVideoAndAudio();
      const video = videoDev ? {deviceId: videoDev} : includeVideo;
      const audio = audioDev ? {deviceId: audioDev} : includeAudio;

      // Do not call getUserMedia again before stopping the previous stream
      const newStream = await navigator.mediaDevices.getUserMedia({video, audio});
      assertNotBlockedCamera(newStream.getTracks());

      // Refresh device list after permissions
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(devices.filter((d) => d.kind === 'audioinput'));
      setVideoDevices(getAllowedVideoDevices(devices));

      if (videoRef.current?.srcObject !== newStream) {
        videoRef.current.srcObject = newStream;
      }

      setAudioTracks(newStream.getAudioTracks());
      setVideoTracks(newStream.getVideoTracks());
      setSetupComplete(true);
      setError(null);
      handleStream(newStream);
    } catch (err) {
      setError(err);
      setSetupComplete(false);
      handleStream(null);
    }
  };

  // Load saved settings on mount and release resources on unmount
  useEffect(() => {
    const settings = getSettings();
    setAudioDevice(settings.audioDevice || null);
    setVideoDevice(settings.videoDevice || null);
    getMediaStream(settings.videoDevice, settings.audioDevice);

    return () => {
      stopVideoAndAudio();
    };
  }, []);

  // Get devices on mount and when device list changes
  useEffect(() => {
    const getDevices = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      setAudioDevices(devices.filter((d) => d.kind === 'audioinput'));
      setVideoDevices(getAllowedVideoDevices(devices));
    };

    getDevices();
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, []);

  const handleSelection = (e) => {
    const {name, value} = e.target;

    let nextAudio = audioDevice;
    let nextVideo = videoDevice;

    if (name === 'audioDevice') {
      setAudioDevice(value);
      nextAudio = value;
    }
    if (name === 'videoDevice') {
      setVideoDevice(value);
      nextVideo = value;
    }

    getMediaStream(nextVideo, nextAudio);
    setSettings({[name]: value});
  };

  const handleSubmit = () => {
    stopVideoAndAudio();
    onSubmit({
      audioDevices,
      videoDevices,
      audioTracks,
      videoTracks,
      setupComplete,
      audioDevice,
      videoDevice,
      error,
      stream,
      enableAIFeedback,
      allowRecordingConsent,
    });
  };

  let videoTrack;
  let audioTrack;
  if (videoTracks.length) {
    videoTrack = videoTracks[0].getSettings().deviceId;
  }
  if (audioTracks.length) {
    audioTrack = audioTracks[0].getSettings().deviceId;
  }

  return (
    <>
      <div className="flex mb-6">
        <div className="w-1/3  mr-4">
          <video
            ref={videoRef}
            autoPlay
            className="h-32 object-cover rounded-lg bg-gray-900"
            muted
            style={{transform: 'scaleX(-1)'}}
          />
          <InputLevel stream={stream} />
        </div>
        <div className="flex flex-col flex-1">
          {includeVideo && (
            <span className="flex items-center bg-gray-200 p-3 mb-3 rounded-lg hover:bg-gray-300">
              <FontAwesomeIcon
                color="#999999"
                icon={error ? faVideoSlash : faVideo}
                className="h-4 w-4 ml-1 mr-3"
              />
              <select
                name="videoDevice"
                className="w-full appearance-none bg-transparent border-0 cursor-pointer text-gray-700 font-medium"
                onChange={handleSelection}
                value={error ? 'error' : videoTrack || ''}
              >
                {videoDevices && videoDevices.map((device, i) => (
                  <option value={device.deviceId} key={device.deviceId}>
                    {device.label !== '' ? device.label : `Video input ${i + 1}`}
                  </option>
                ))}
                {error && (
                  <option value="error">
                    {error.name === BLOCKED_CAMERA_ERROR ? 'Camera not allowed' : 'No video found'}
                  </option>
                )}
              </select>
            </span>
          )}
          {includeAudio && (
            <span className="flex items-center bg-gray-200 p-3 rounded-lg hover:bg-gray-300">
              <FontAwesomeIcon
                color="#999999"
                icon={error ? faMicrophoneSlash : faMicrophone}
                className="h-4 w-4 ml-1 mr-4"
              />
              <select
                name="audioDevice"
                className="w-full appearance-none bg-transparent border-0 cursor-pointer text-gray-700 font-medium"
                onChange={handleSelection}
                value={error ? 'error' : audioTrack || ''}
              >
                {audioDevices && audioDevices.map((device, i) => (
                  <option value={device.deviceId} key={device.deviceId}>
                    {device.label !== '' ? device.label : `Audio input ${i + 1}`}
                  </option>
                ))}
                {error && <option value="error">No audio found</option>}
              </select>
            </span>
          )}
        </div>
      </div>

      {!hideAIFeedbackToggle && (
        <DuetAIFeedbackToggle
          value={enableAIFeedback}
          onChange={setEnableAIFeedback}
        />
      )}

      {showRecordingConsent && canGiveRecordingConsent(currentUser) && (
        <RecordingConsentCheckbox
          checked={allowRecordingConsent}
          onChange={setAllowRecordingConsent}
        />
      )}

      <div className="flex justify-between items-center">
        {hideTroubleshooting ? (
          <div />
        ) : (
          <Link
            href="/practice/troubleshooting"
            target="_blank"
            className="font-medium text-gray-700"
          >
            Troubleshooting
          </Link>
        )}
        <Tooltip
          content="You'll be able to join once you set up your camera and microphone."
          placement="bottom"
          disabled={setupComplete}
        >
          <span>
            <Button
              onClick={handleSubmit}
              disabled={!setupComplete}
              size={ButtonSizes.LARGE}
            >
              {submitText || 'Join the waiting room'}
            </Button>
          </span>
        </Tooltip>
      </div>
    </>
  );
};

export default DuetSettings;
